import {
  type FC, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useConfirmationModal } from '~/_shared/components/modal/confirmation/useConfirmationModal';
import {
  useGetEntityAccesses,
  useUpdateEntityAccess,
} from '~/_shared/entityAccess/entityAccess.hooks';
import type { EntityAccessInvitation } from '~/_shared/entityAccess/entityAccess.invitations.repository';
import {
  type EntityAccess,
  EntityAccessLevel,
  EntityType,
  SharedEntityType,
} from '~/_shared/entityAccess/entityAccess.repository';
import {
  useGetMapAccessInvitationsCreatedByCurrentUser, useManageEntityAccessInvitations,
} from '~/_shared/entityAccess/entityAccessInvitations.hooks';
import { useChangeMapOwner } from '~/_shared/entityAccess/useChangeMapOwner';
import { useTranslation } from '~/_shared/utils/hooks';
import { useGetMembers } from '~/clientTeamManagement/teamManagementModal/useTeamManagement';
import { type ModalProps } from '~/modal/modalType.enum';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useUserIdSelector } from '~/store/userData/userData.selectors';
import { AppErrorType } from '../../appError/appErrorType.enum';
import { teamShareMapPermissionToEntityAccessLevel } from './teamShareMap.helpers';
import { TeamShareMapPermission } from './teamShareMap.types';
import { TeamShareMapModalComponent } from './teamShareMapModal.component';
import { useRemoveMemberFromMap } from './useRemoveMemberFromMap.hook';

export type MapAccessData = Readonly<{
  entityAccess?: EntityAccess;
  invitation?: EntityAccessInvitation;
  name: string | null;
  email: string | null;
}>;

export type TeamShareMapModalContainerProps = ModalProps<{
  isSnapshot: boolean;
  mapId: number;
  mapName: string;
}>;

export const TeamShareMapModalContainer: FC<TeamShareMapModalContainerProps> = (props) => {
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const dispatch = useDispatch();
  const userId = useUserIdSelector();
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();

  const {
    update: updateEntityAccess,
    isLoading: isUpdatingEntityAccess,
  } = useUpdateEntityAccess();

  const {
    isLoading: isDeletingEntityAccess,
    removeUserFromMapRequest,
  } = useRemoveMemberFromMap();

  const {
    update: changeMapOwner,
    isLoading: isChangingOwner,
  } = useChangeMapOwner();

  const {
    isLoading: isGettingEntityAccesses,
    data: entityAccessesData,
    invokeAjax: getEntityAccesses,
  } = useGetEntityAccesses({
    clientId,
    entityType: EntityType.MAP,
    sharedEntityType: SharedEntityType.USER,
    entityId: props.mapId,
  });

  const {
    isLoading: isGettingMembers,
    data: membersData,
    invokeAjax: getMembers,
  } = useGetMembers(clientId);

  const {
    revokeEntityAccessInvitation,
    isLoading: isManageInvitationsLoading,
  } = useManageEntityAccessInvitations();

  const {
    getMapAccessInvitations,
    mapInvitationsData,
    isLoading: isGettingInvitations,
  } = useGetMapAccessInvitationsCreatedByCurrentUser(props.mapId);

  const isLoading = useMemo(() => (
    isUpdatingEntityAccess || isDeletingEntityAccess || isChangingOwner || isGettingEntityAccesses
    || isGettingMembers || isManageInvitationsLoading || isGettingInvitations
  ), [
    isUpdatingEntityAccess, isDeletingEntityAccess, isChangingOwner, isGettingEntityAccesses,
    isGettingMembers, isManageInvitationsLoading, isGettingInvitations,
  ]);

  useEffect(() => {
    getMembers();
  }, [getMembers]);

  useEffect(() => {
    getEntityAccesses();
  }, [getEntityAccesses]);

  useEffect(() => {
    getMapAccessInvitations();
  }, [getMapAccessInvitations]);

  const mapAccessData = useMemo(() => {
    if (!membersData || !entityAccessesData || !userId) {
      return null;
    }

    const entityAccessDataWithoutOwner = entityAccessesData.list.filter(
      (item) => item.ownerId !== item.sharedEntityId
    );

    const entityAccessDataWithOwner = [
      {
        id: -1,
        ownerId: userId,
        entityType: EntityType.MAP,
        entityId: props.mapId,
        sharedEntityType: SharedEntityType.USER,
        sharedEntityId: userId,
        accessLevel: EntityAccessLevel.WRITE,
        createdAt: 'created_at',
        updatedAt: 'updated_at',
      },
      ...entityAccessDataWithoutOwner,
    ];

    const getMemberDataForSharedEntityId = (sharedEntityId: number) => {
      const member = membersData.members.find(
        (memberItem) => memberItem.id?.toString() === sharedEntityId.toString()
      );
      return {
        name: member?.name || t('Unknown'),
        email: member?.email || t('Unknown'),
      };
    };

    const entityAccessData = entityAccessDataWithOwner.map((item) => ({
      entityAccess: item,
      ...getMemberDataForSharedEntityId(item.sharedEntityId),
    }));

    const invitationsData = mapInvitationsData.map((item) => ({
      invitation: item,
      ...getMemberDataForSharedEntityId(item.sharedEntityId),
    }));

    return [...entityAccessData, ...invitationsData];
  }, [membersData, entityAccessesData, userId, props.mapId, mapInvitationsData, t]);

  const dispatchErrorWarning = useCallback(() => {
    dispatch(
      createAppError({
        type: AppErrorType.General,
        title: t('Error'),
        content: t('teamShareMap.editError'),
        errorTitle: t('teamShareMap.editErrorTitle'),
      })
    );
  }, [dispatch, t]);

  const handleChangeOwner = useCallback((item: EntityAccess) => {
    if (!clientId || !userId) {
      return;
    }

    openConfirmationModal({
      title: t('Warning'),
      confirmCaption: t('Proceed'),
      text: t('teamShareMap.changeOwner.warning'),
      isConfirmButtonDestructive: true,
      onCancel: () => {
        closeConfirmationModal();
      },
      onConfirm: () => {
        changeMapOwner(
          clientId,
          props.mapId,
          item.sharedEntityId,
        ).then(() => {
          location.reload();
        }).catch(() => {
          dispatchErrorWarning();
        });
        closeConfirmationModal();
      },
    });
  }, [changeMapOwner, clientId, closeConfirmationModal, dispatchErrorWarning, openConfirmationModal, props.mapId, t, userId]);

  const onUpdateAccess = useCallback((item: EntityAccess, newPermission: TeamShareMapPermission) => {
    if (!clientId || !userId) {
      return;
    }

    if (newPermission === TeamShareMapPermission.Owner) {
      handleChangeOwner(item);
    }
    else {
      updateEntityAccess(clientId, item.id, teamShareMapPermissionToEntityAccessLevel(newPermission))
        .then(() => {
          if (item.sharedEntityId === userId) {
            location.reload();
          }
          else {
            getEntityAccesses();
          }
        })
        .catch(() => {
          dispatchErrorWarning();
        });
    }

  }, [clientId, dispatchErrorWarning, getEntityAccesses, handleChangeOwner, updateEntityAccess, userId]);

  const onDeleteAccess = useCallback((item: EntityAccess) => {
    removeUserFromMapRequest({
      entityAccessId: item.id,
      isCurrentUserSharee: false,
      onSuccess: () => {
        getEntityAccesses();
      },
    });
  }, [getEntityAccesses, removeUserFromMapRequest]);

  const onDeleteInvitation = useCallback((item: EntityAccessInvitation) => {
    revokeEntityAccessInvitation(item.id)
      .finally(() => {
        getMapAccessInvitations();
        getEntityAccesses();
      });
  }, [revokeEntityAccessInvitation, getEntityAccesses, getMapAccessInvitations]);

  const onMapAccessChange = useCallback(() => {
    getEntityAccesses();
    getMapAccessInvitations();
  }, [getEntityAccesses, getMapAccessInvitations]);

  return (
    <TeamShareMapModalComponent
      {...props}
      currentUserId={userId}
      isLoading={isLoading}
      mapAccessData={mapAccessData || []}
      onMapAccessChange={onMapAccessChange}
      onUpdateAccess={onUpdateAccess}
      onDeleteAccess={onDeleteAccess}
      onDeleteInvitation={onDeleteInvitation}
    />
  );
};
